import { useState, useCallback } from 'react'
import { useAngularServices } from '@/react/components'

interface UseCategoriesOptions {
  onSuccess?: () => void
  onError?: (error: string) => void
}

interface UseCategoriesReturn {
  isDeleting: boolean
  isUpdating: boolean
  deleteCategory: (categoryId: number) => Promise<void>
  updateCategoryName: (categoryId: number, newName: string) => Promise<void>
}

export const useHandleCategories = (
  options: UseCategoriesOptions = {},
): UseCategoriesReturn => {
  const { onSuccess, onError } = options

  const [isDeleting, setIsDeleting] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const { Api } = useAngularServices()

  const deleteCategory = useCallback(
    async (categoryId: number) => {
      setIsDeleting(true)

      try {
        await Api.delete(`categories/${categoryId}`, {})
        onSuccess?.()
      } catch (error) {
        onError?.(error as string)
        throw error
      } finally {
        setIsDeleting(false)
      }
    },
    [Api, onSuccess, onError],
  )

  const updateCategoryName = useCallback(
    async (categoryId: number, newName: string) => {
      setIsUpdating(true)

      try {
        await Api.patch(`categories/${categoryId}`, {
          name: newName,
          ids: categoryId.toString(),
        })
        onSuccess?.()
      } catch (error) {
        onError?.(error as string)
        throw error
      } finally {
        setIsUpdating(false)
      }
    },
    [Api, onSuccess, onError],
  )

  return {
    isDeleting,
    isUpdating,
    deleteCategory,
    updateCategoryName,
  }
}
